'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { useLocationFilter } from '@/hooks/use-location-filter';
import { cn } from '@/lib/utils';

import { LocationComboBox } from './location-filter-combobox';

type LocationFilterDrawerProps = {
  className?: string;
};

const snapPoints = [0.6, 0.9];

export const LocationFilterDrawer = ({
  className,
}: LocationFilterDrawerProps) => {
  const {
    value,
    setValue,
    searchQuery,
    locations,
    isPending,
    isSearching,
    error,
    handleSearch,
    clearLocation,
  } = useLocationFilter();
  const [activeSnapPoint, setActiveSnapPoint] = useState<
    number | string | null
  >(snapPoints[0]);

  const [isOpen, setIsOpen] = useState(false);

  const getButtonText = () => {
    if (!value || value === 'any') {
      return 'Any location';
    }
    return value;
  };

  return (
    <Drawer
      nested
      autoFocus
      fadeFromIndex={0}
      snapPoints={snapPoints}
      activeSnapPoint={activeSnapPoint}
      setActiveSnapPoint={setActiveSnapPoint}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DrawerTrigger asChild>
        <Button
          variant='outline-brand'
          className={cn(
            'w-full justify-start font-normal',
            value === null && 'text-muted-foreground',
            className,
          )}
        >
          {getButtonText()}
          <ChevronDown className='ml-auto size-4 shrink-0 opacity-50' />
        </Button>
      </DrawerTrigger>
      <DrawerContent className='h-full max-h-[80vh]'>
        <DrawerHeader className='sr-only'>
          <DrawerTitle>Select location</DrawerTitle>
          <DrawerDescription>
            Enter a location and select it from the list.
          </DrawerDescription>
        </DrawerHeader>
        <div className='mt-4 border-t'>
          <LocationComboBox
            value={value}
            locations={locations}
            searchQuery={searchQuery}
            isPending={isPending}
            isSearching={isSearching}
            error={error}
            onSearch={handleSearch}
            onClear={() => {
              clearLocation();
              setIsOpen(false);
            }}
            onSelect={(loc) => {
              setValue(loc);
              setIsOpen(false);
            }}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
};
